import React, { useState, useEffect } from 'react';
import treeService from '../services/treeService';
import { motion } from 'framer-motion';
import PageTransition from '../components/PageTransition'; // <-- NEW

const UserStatsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTrees: 0,
    totalCarbon: 0,
    speciesCount: 0,
    mostCommonSpecies: 'N/A',
    oldestTreeDate: null,
    // Financial Stats
    totalEarned: 0,
    pendingAmount: 0,
    totalDonated: 0
  });

  const user = JSON.parse(localStorage.getItem('user'));
  const CURRENCY = 'KES'; // Kenyan Shillings
  const RATE_PER_MONTH = 100; // Earnings per tree per month

  useEffect(() => {
    const calculateImpact = async () => {
      try {
        const trees = await treeService.getMyTrees();
        
        // 1. Total Trees
        const totalTrees = trees.length;

        // Variables for aggregation
        let totalCarbon = 0;
        let grossEarnings = 0;
        let currentMonthEarnings = 0; // This will be our "Pending" amount
        const speciesMap = {};
        let oldestDate = new Date();

        trees.forEach(tree => {
          const start = new Date(tree.plantingDate);
          const now = new Date();
          
          // Time Diffs
          const diffTime = Math.abs(now - start);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          const diffMonths = diffDays / 30; // Approx months

          // Carbon Math (22kg/year)
          const treeCarbon = (diffDays / 365) * 22; 
          totalCarbon += treeCarbon;

          // Financial Math
          // Total lifetime earnings for this tree
          const treeLifetimeEarnings = diffMonths * RATE_PER_MONTH;
          grossEarnings += treeLifetimeEarnings;

          // Calculate pending (just the last month of earnings)
          if (diffMonths >= 1) {
            currentMonthEarnings += RATE_PER_MONTH;
          } else {
            // If tree is less than a month old, all its earnings are pending
            currentMonthEarnings += treeLifetimeEarnings;
          }

          // Species Count
          speciesMap[tree.species] = (speciesMap[tree.species] || 0) + 1;

          // Oldest Tree
          if (start < oldestDate) oldestDate = start;
        });

        // Financial Breakdowns
        const totalDonated = grossEarnings * 0.01; // 1% to Society
        const totalEarnedCumulative = grossEarnings; // Total generated
        const pendingAmount = currentMonthEarnings; // Not yet paid out

        // Most Common Species Logic
        let mostCommon = 'N/A';
        let maxCount = 0;
        Object.entries(speciesMap).forEach(([name, count]) => {
          if (count > maxCount) {
            maxCount = count;
          }
        });

        setStats({
          totalTrees,
          totalCarbon: totalCarbon.toFixed(2),
          speciesCount: Object.keys(speciesMap).length,
          mostCommonSpecies: mostCommon,
          oldestTreeDate: totalTrees > 0 ? oldestDate.toLocaleDateString() : 'N/A',
          // Set Financials (formatted)
          totalEarned: totalEarnedCumulative.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}),
          pendingAmount: pendingAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}),
          totalDonated: totalDonated.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
        });
        setIsLoading(false);

      } catch (err) {
        console.error(err);
        setIsLoading(false);
      }
    };

    calculateImpact();
  }, []);

  if (isLoading) return <div className="text-center mt-20 text-brand-green font-bold animate-pulse">Calculating Impact & Earnings...</div>;

  return (
    <PageTransition> {/* <-- WRAPPER ADDED */}
      <div className="max-w-6xl mx-auto space-y-10">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-brand-green-dark mb-2">Your Impact Report</h1>
          <p className="text-gray-600">
            Tracking the environmental and financial growth of {user?.name.split(' ')[0]}'s portfolio.
          </p>
        </div>

        {/* SECTION 1: ENVIRONMENTAL IMPACT */}
        <div>
          <h2 className="text-xl font-bold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">Environmental Metrics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Total Trees */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-8 rounded-xl shadow-lg border-l-8 border-brand-green flex flex-col justify-between"
            >
              <div>
                <div className="text-4xl mb-2">🌳</div>
                <h3 className="text-gray-500 font-bold uppercase tracking-wider text-sm">Total Trees Planted</h3>
              </div>
              <p className="text-5xl font-bold text-brand-gray mt-4">{stats.totalTrees}</p>
            </motion.div>

            {/* Carbon Sequestered */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-brand-green-dark p-8 rounded-xl shadow-lg border-l-8 border-brand-green-light flex flex-col justify-between text-white"
            >
              <div>
                <div className="text-4xl mb-2">☁️</div>
                <h3 className="text-brand-green-light font-bold uppercase tracking-wider text-sm">CO₂ Removed</h3>
              </div>
              <p className="text-5xl font-bold mt-4">{stats.totalCarbon} <span className="text-2xl">kg</span></p>
            </motion.div>

            {/* Biodiversity */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-8 rounded-xl shadow-lg border-l-8 border-brand-green flex flex-col justify-between"
            >
              <div>
                <div className="text-4xl mb-2">🧬</div>
                <h3 className="text-gray-500 font-bold uppercase tracking-wider text-sm">Biodiversity Score</h3>
              </div>
              <p className="text-5xl font-bold text-brand-gray mt-4">{stats.speciesCount} <span className="text-xl font-normal text-gray-400">Species</span></p>
            </motion.div>
          </div>
        </div>

        {/* SECTION 2: FINANCIAL & SOCIAL IMPACT */}
        <div>
          <h2 className="text-xl font-bold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">Wallet & Society</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Total Earned */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-white p-8 rounded-xl shadow-lg border-t-8 border-brand-gold relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 bg-brand-gold text-white text-xs font-bold px-2 py-1 rounded-bl">LIFETIME</div>
              <div className="text-4xl mb-2">💰</div>
              <h3 className="text-gray-500 font-bold uppercase tracking-wider text-sm">Total Earned</h3>
              <p className="text-4xl font-bold text-brand-green-dark mt-2">
                <span className="text-lg text-gray-400 font-normal mr-1">{CURRENCY}</span>
                {stats.totalEarned}
              </p>
            </motion.div>

            {/* Pending Payout */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-white p-8 rounded-xl shadow-lg border-t-8 border-gray-300 relative"
            >
              <div className="absolute top-0 right-0 bg-gray-300 text-gray-600 text-xs font-bold px-2 py-1 rounded-bl">PENDING</div>
              <div className="text-4xl mb-2">⏳</div>
              <h3 className="text-gray-500 font-bold uppercase tracking-wider text-sm">To Be Paid</h3>
              <p className="text-4xl font-bold text-gray-600 mt-2">
                <span className="text-lg text-gray-400 font-normal mr-1">{CURRENCY}</span>
                {stats.pendingAmount}
              </p>
            </motion.div>

            {/* Charity Donation */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-brand-gold to-yellow-500 p-8 rounded-xl shadow-lg text-white relative"
            >
              <div className="absolute top-0 right-0 bg-white/20 text-white text-xs font-bold px-2 py-1 rounded-bl">1% PLEDGE</div>
              <div className="text-4xl mb-2">🤝</div>
              <h3 className="text-white/80 font-bold uppercase tracking-wider text-sm">Donated to Society</h3>
              <p className="text-4xl font-bold mt-2">
                <span className="text-lg text-white/60 font-normal mr-1">{CURRENCY}</span>
                {stats.totalDonated}
              </p>
            </motion.div>
          </div>
        </div>

        {/* SECTION 3: FUN FACTS */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="bg-gray-50 p-6 rounded-lg border border-gray-200 flex flex-col md:flex-row justify-around items-center text-center md:text-left"
        >
          <div className="mb-4 md:mb-0">
            <h4 className="text-gray-500 font-bold text-xs uppercase">Most Planted Species</h4>
            <p className="text-xl font-bold text-brand-green-dark">{stats.mostCommonSpecies}</p>
          </div>
          <div className="h-8 w-px bg-gray-300 hidden md:block"></div>
          <div>
            <h4 className="text-gray-500 font-bold text-xs uppercase">Portfolio Started</h4>
            <p className="text-xl font-bold text-brand-green-dark">{stats.oldestTreeDate}</p>
          </div>
        </motion.div>
      </div>
    </PageTransition> /* <-- WRAPPER ADDED */
  );
}

export default UserStatsPage;